import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase, db } from '../supabase'
import { useNavigate } from 'react-router-dom'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate();



  const createProfile = useCallback(async (userId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const profileData = {
        id: userId,
        email: user.email,
        name: user.user_metadata?.full_name || user.email?.split('@')[0],
        avatar_url: user.user_metadata?.avatar_url,
        bio: '',
        status: 'Just getting started!',
        achievements: [],
        friends: []
      }
      const { data, error } = await supabase
        .from('users')
        .insert(profileData)
        .select()
        .single()
      if (error) throw error
      setProfile(data)
    } catch (error) {
      console.error('Error creating profile:', error)
    }
  }, [])

  const loadProfile = useCallback(async (userId) => {
    try {
      const profileData = await db.getUserProfile(userId)
      setProfile(profileData)
    } catch (error) {
      console.error('Error loading profile:', error)
      if (error.code === 'PGRST116') {
        try {
          await createProfile(userId)
        } catch (createError) {
          console.error('Error creating profile:', createError)
        }
      }
    }
  }, [createProfile])

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoading(false)
    }, 5000) // 5 second timeout

    // Handle OAuth hash fragment after login
    if (window.location.hash.includes('access_token')) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        clearTimeout(timeoutId)
        setUser(session?.user ?? null)
        if (session?.user) {
          loadProfile(session.user.id)
          window.location.hash = '' // Clean up the URL
          navigate('/') // Redirect to app/dashboard
        }
        setLoading(false)
      }).catch(error => {
        clearTimeout(timeoutId)
        console.error('Error getting session:', error)
        setLoading(false)
      })
      return
    }
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeoutId)
      setUser(session?.user ?? null)
      if (session?.user) {
        loadProfile(session.user.id)
        navigate('/')
      }
      setLoading(false)
    }).catch(error => {
      clearTimeout(timeoutId)
      console.error('Error getting initial session:', error)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        clearTimeout(timeoutId)
        setUser(session?.user ?? null)
        if (session?.user) {
          await loadProfile(session.user.id)
          navigate('/')
        } else {
          setProfile(null)
        }
        setLoading(false)
      }
    )

    return () => {
      clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [loadProfile, navigate])

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      })
      if (error) throw error
    } catch (error) {
      console.error('Error signing in with Google:', error)
      throw error
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }

  const updateProfile = async (updates) => {
    try {
      const updatedProfile = await db.updateUserProfile(user.id, updates)
      setProfile(updatedProfile)
      return updatedProfile
    } catch (error) {
      console.error('Error updating profile:', error)
      throw error
    }
  }

  const value = {
    user,
    profile,
    loading,
    signInWithGoogle,
    signOut,
    updateProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 